import { forgetCmsImpressions } from "@/services/activity/cmsActivity";
import {
  getJsonWebTokenLogin,
  noteJsonWebTokenReceived,
  startNewActivitySession,
} from "@/services/activity/portalActivitySession";
import { flushActivity, forgetHeldSends } from "@/services/activity/portalActivityTracker";

export interface AuthActionContext {
  name: string;
  args: unknown[];
  after: (callback: () => void) => void;
}

const SESSION_ACTIONS = ["clearState", "setSelectedAccount", "setWebTokens"];

function isStoredKeptSession(jsonWebToken: string | undefined): boolean {
  try {
    return !!jsonWebToken && JSON.parse(localStorage.getItem("tokens") ?? "null")?.jsonWebToken === jsonWebToken;
  } catch {
    return false;
  }
}

// Sends what was recorded under the session before it changes hands, and starts a new
// activity session once a different login, or no one, holds the tab. A token refresh
// keeps the same login, so what is queued goes afterwards under the new token: the old
// one has expired, and the server would refuse it.
//
// Signing out while the token has expired drops whatever was held for a new one: a
// refresh then would put tokens back for a login that has just signed out. Little is
// ever held, because the tracker sends held events for the server to judge as soon as
// the grower does something.
export function onAuthAction(
  { name, args, after }: AuthActionContext,
  currentLogin: () => string
) {
  if (!SESSION_ACTIONS.includes(name)) {
    return;
  }

  const loginBefore = currentLogin();
  const tokens = args[0] as { jsonWebToken?: string } | undefined;

  // tokens taken up from another tab's kept session are as old as that tab's refresh, so only those the API has just
  // issued, not yet stored, say what the server's clock reads
  if (name === "setWebTokens" && !isStoredKeptSession(tokens?.jsonWebToken)) {
    noteJsonWebTokenReceived(tokens?.jsonWebToken, Date.now());
  }

  if (
    name === "setWebTokens" &&
    loginBefore !== "" &&
    getJsonWebTokenLogin(tokens?.jsonWebToken) === loginBefore
  ) {
    after(() => void flushActivity());
    return;
  }

  void flushActivity(name === "setSelectedAccount" ? "accountChange" : "sessionChange");

  after(() => {
    if (currentLogin() !== loginBefore) {
      forgetHeldSends();
      startNewActivitySession();
      forgetCmsImpressions();
    }
  });
}
